import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronDown, FlaskConical, MapPin, Sparkles, Tag } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { request } from "@/lib/api";

// Services carrying advertised copy, in the order they should read. Anything
// else the client has is a benefit nobody has written an offer for yet, and is
// left off this screen rather than described on their behalf.
const offerOrder = ["dental", "vision", "employee_health_check"];

function groupByCity(locations) {
  const cities = new Map();
  for (const location of locations) {
    if (!cities.has(location.city)) cities.set(location.city, []);
    cities.get(location.city).push(location);
  }
  return [...cities.entries()];
}

function OfferCard({ service, offer }) {
  const [showClinics, setShowClinics] = useState(false);
  const cities = useMemo(() => groupByCity(offer.locations || []), [offer.locations]);
  const clinicCount = offer.locations?.length || 0;

  return (
    <article className="offer-card" data-testid={`offer-${service}`}>
      <div className="offer-card__head">
        <Sparkles size={22} />
        <h2>{offer.offer_label}</h2>
      </div>

      <ul className="offer-benefits" data-testid={`offer-${service}-benefits`}>
        {offer.benefits.map((benefit) => (
          <li key={benefit}>
            <CheckCircle2 size={17} />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      {clinicCount > 0 && (
        <>
          <Button asChild data-testid={`offer-${service}-book-button`}>
            <Link to={`/free-services?service=${service}`}>Book your free consultation</Link>
          </Button>

          <button
            type="button"
            className="offer-clinics-toggle"
            onClick={() => setShowClinics((current) => !current)}
            aria-expanded={showClinics}
            data-testid={`offer-${service}-locations-toggle`}
          >
            <MapPin size={16} />
            {clinicCount} clinic{clinicCount === 1 ? "" : "s"} across{" "}
            {cities.map(([city]) => city).join(", ")}
            <ChevronDown size={16} className={showClinics ? "is-open" : ""} />
          </button>

          {showClinics && (
            <div className="offer-clinics" data-testid={`offer-${service}-locations`}>
              {cities.map(([city, clinics]) => (
                <div key={city}>
                  <p className="eyebrow">{city}</p>
                  <ul>
                    {clinics.map((clinic) => (
                      <li key={clinic.id} data-testid={`offer-clinic-${clinic.id}`}>
                        <strong>{clinic.area}</strong>
                        {/* Four of these share an area, so the address is the
                            only thing that says which one you're looking at. */}
                        <small>{clinic.address || clinic.name}</small>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </article>
  );
}

function LabTestsOffer({ summary }) {
  return (
    <article className="offer-card" data-testid="offer-lab-tests">
      <div className="offer-card__head">
        <FlaskConical size={22} />
        <h2>Discounted Lab Tests</h2>
      </div>
      <ul className="offer-benefits" data-testid="offer-lab-tests-benefits">
        <li>
          <CheckCircle2 size={17} />
          <span>
            Up to {summary.max_discount_percent}% off on {summary.count.toLocaleString("en-IN")} pathology tests
          </span>
        </li>
        <li><CheckCircle2 size={17} /><span>Samples collected from your home, on a weekday that suits you</span></li>
        <li><CheckCircle2 size={17} /><span>Order for yourself or for family — from {`₹${Math.round(summary.from_price)}`} a test</span></li>
      </ul>
      <Button asChild data-testid="offer-lab-tests-browse-button">
        <Link to="/lab-tests"><Tag size={16} />Browse tests</Link>
      </Button>
    </article>
  );
}

export default function OffersPage() {
  const { token } = useAuth();
  const [services, setServices] = useState(null);
  const [labSummary, setLabSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    request("/complimentary-bookings/services", {}, token)
      .then(setServices)
      .catch(() => setError("We could not load your benefits just now. Please try again shortly."));
  }, [token]);

  useEffect(() => {
    // Its own call: the catalogue is a different shape from the clinic-based
    // benefits, and an empty one simply means no list has been loaded yet.
    request("/lab-tests/summary", {}, token)
      .then((result) => setLabSummary(result?.count ? result : null))
      .catch(() => setLabSummary(null));
  }, [token]);

  const offers = offerOrder
    .map((service) => [service, services?.[service]])
    .filter(([, offer]) => offer?.offer_label && offer.benefits?.length);

  return (
    <section className="page-stack" data-testid="offers-page">
      <div className="page-heading">
        <p className="eyebrow">Exclusive benefits</p>
        <h1>More ways to care for yourself.</h1>
      </div>

      {error && <p className="form-error" role="alert" data-testid="offers-error">{error}</p>}

      {offers.map(([service, offer]) => (
        <OfferCard key={service} service={service} offer={offer} />
      ))}

      {labSummary && <LabTestsOffer summary={labSummary} />}

      {services && !offers.length && !labSummary && !error && (
        <div className="empty-updates" data-testid="offers-coming-soon">
          <Sparkles size={28} />
          <p>Your benefits will appear here as they are confirmed.</p>
        </div>
      )}
    </section>
  );
}
